import DateFnsUtils from "@date-io/date-fns"
import { Button, CssBaseline, ThemeProvider } from "@material-ui/core"
import { createTheme } from "@material-ui/core/styles"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"
import { SnackbarProvider, useSnackbar } from "notistack"
import React, { useEffect, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { HashRouter, Redirect, Route, Switch, useLocation } from "react-router-dom"
import SPARK from "spark-core"
import StackTrace from "stacktrace-js"
import Root from "./Admin/Index"
import { Service } from "./DBService/DBService"
import Login from "./Login"
import Messages from "./Messages"
import NavigationLayout from "./NavigationLayout"
import { sensorEventUpdate } from "./Participant/BottomMenu"
import Participant from "./Participant/Index"
import NotificationPage from "./Participant/NotificationPage"
import PreventPage from "./Participant/Prevent/PreventPage"
import Researcher from "./Researcher/Index"
import Activity from "./Researcher/ProjectView/ActivityList/Activity"
import Project from "./Researcher/ProjectView/Index"
import PatientProfile from "./Researcher/ProjectView/ParticipantList/Profile/PatientProfilePage"
import DataPortal from "./data_portal/DataPortal"

import schema from "../adaptable/theme.json"
import Permissions from "./Participant/Permissions"
import Trigger from "./Researcher/ProjectView/TriggerList/Trigger"

function ErrorFallback({ error }) {
  const [trace, setTrace] = useState([])
  useEffect(() => {
    StackTrace.fromError(error).then(setTrace)
  }, [])
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        border: "none",
        zIndex: 2147483647,
        padding: "0.5rem",
        fontFamily: "Consolas, Menlo, monospace",
        whiteSpace: "pre-wrap",
        lineHeight: 1.5,
        fontSize: "12px",
        color: "rgb(232, 59, 70)",
        background: "rgb(53, 53, 53)",
      }}
    >
      <pre>
        <code style={{ fontSize: "16px" }}>
          {error.message.match(/^\w*:/) || !error.name ? error.message : error.name + ": " + error.message}
        </code>
        <br />
        <code style={{ color: "#fff" }}>
          {trace.length > 0 ? trace.map((x) => x.toString()).join("\n") : "Generating stacktrace..."}
        </code>
        <br />
        <code>
          SPARK Version: `v${process.env.REACT_APP_GIT_NUM} (${process.env.REACT_APP_GIT_SHA})`
        </code>
        <br />
        <a style={{ fontSize: "16px" }} href="javascript:void(0)" onClick={() => window.location.reload()}>
          Back to page
        </a>
      </pre>
    </div>
  )
}
function PageTitle({ children, ...props }) {
  useEffect(() => {
    document.title = `${typeof children === "string" ? children : ""}`
  })
  return <React.Fragment>{null}</React.Fragment>
}
export const changeCase = (text) => {
  if (!!text) {
    let result = text.replace(/([A-Z])/g, " $1")
    result = text.replace(/_/g, " ")
    result = result.charAt(0).toUpperCase() + result.slice(1)
    return result
  }
  return ""
}
function AppRouter({ ...props }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const search = useLocation().search

  // To set page titile for active tab for menu
  let activeTab = (newTab?: string, participantId?: string) => {
    if (window.location.href.indexOf("participant") >= 0) {
      setState((state) => ({
        ...state,
        activeTab: newTab,
      }))
      window.location.href = `/#/participant/${participantId}/${newTab.toLowerCase()}`
    }
  }

  let changeMode = (_mode: string) => {
    setState((state) => ({
      ...state,
      mode: _mode,
    }))
  }

  const [state, setState] = useState({
    identity: SPARK.Auth._me,
    auth: SPARK.Auth._auth,
    authType: SPARK.Auth._type,
    lastDomain: process.env.REACT_APP_API_URL, // "localhost:3000", // 10.0.2.2:3000 when running Android emulator
    activeTab: null,
    surveyDone: false,
    welcome: true,
    messageCount: 0,
    mode: "simple", // defaults to simple mode, when logged in checks for researcher type in the local storage
    adminType: "admin",
    professionalRole: null, // role in organization
  })
  const [store, setStore] = useState({ researchers: [], participants: [], projects: [] }) // TODO: add organization[] in store?
  const [name, setName] = useState("")
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const storeRef = useRef([])
  const [showDemoMessage, setShowDemoMessage] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    let query = window.location.hash.split("?")
    if (!!query && query.length > 1) {
      let src = Object.fromEntries(new URLSearchParams(query[1]))["src"]
      if (typeof src === "string" && src.length > 0) {
        enqueueSnackbar(`${t("You're using the src server to log into Spark.", { src: src })}`, { variant: "info" })
      }
      let values = Object.fromEntries(new URLSearchParams(query[1]))
      if (!!values["mode"]) {
        refreshPage()
        return
      }
      let a = Object.fromEntries(new URLSearchParams(query[1]))["a"]
      if (a === undefined) window.location.href = "/#/"
      let x = atob(a).split(":")
      //
      reset({
        id: x[0],
        password: x[1],
        serverAddress:
          x.length > 2 && typeof x[2] !== "undefined"
            ? x[2] + (x.length > 3 && typeof x[3] !== "undefined" ? ":" + x[3] : "")
            : "api.spark.digital",
        production: true,
      }).then((x) => {
        window.location.href = query[0]
      })
    } else if (!state.identity) {
      refreshPage()
    }
    // FIXME: id prop is called on undefined/null object
    // document.addEventListener("visibilitychange", function logData() {
    //   if (document.visibilityState === "hidden") {
    //     sensorEventUpdate(null, (SPARK.Auth._me as any)?.id, null)
    //   } else {
    //     let hrefloc = window.location.href.split("/")[window.location.href.split("/").length - 1]
    //     hrefloc.split("?").length > 1
    //       ? sensorEventUpdate(state.activeTab, (SPARK.Auth._me as any)?.id, hrefloc.split("?")[0])
    //       : sensorEventUpdate(hrefloc.split("?")[0], (SPARK.Auth._me as any).id, null)
    //   }
    // })
    window.addEventListener("beforeinstallprompt", (e) => setDeferredPrompt(e))
  }, [])

  const refreshPage = () => {
    SPARK.Auth.refresh_identity().then((x) => {
      getAdminType()
      setState((state) => ({
        ...state,
        identity: SPARK.Auth._me,
        auth: SPARK.Auth._auth,
        authType: SPARK.Auth._type,
        production: true,
      }))
    })
  }

  const getAdminType = () => {
    SPARK.Type.getAttachment(null, "spark.dashboard.admin_permissions").then((res: any) => {
      if (!!res.data) {
        let checked = false
        Object.keys(res.data).map((key) => {
          if (res.data[key].hasOwnProperty((SPARK.Auth._auth as any).id)) {
            const id = Object.keys(res.data[key])[0]
            checked = true
            setState((state) => ({
              ...state,
              adminType:
                res.data[key][id] === "view" ? "practice_lead" : res.data[key][id] === "edit" ? "user_admin" : "admin",
            }))
          }
        })
        if (!checked) {
          setState((state) => ({
            ...state,
            adminType: "admin",
          }))
        }
      } else {
        setState((state) => ({
          ...state,
          adminType: "admin",
        }))
      }
    })
  }

  // useEffect(() => {
  //   if (!deferredPrompt) return
  //   enqueueSnackbar(`${t("Add Spark to your home screen?")}`, {
  //     variant: "info",
  //     persist: true,
  //     action: (key) => (
  //       <React.Fragment>
  //         <Button style={{ color: "#fff" }} onClick={promptInstall}>
  //           {`${t("Install")}`}
  //         </Button>
  //         <Button style={{ color: "#fff" }} onClick={() => closeSnackbar(key)}>
  //           {`${t("Dismiss")}`}
  //         </Button>
  //       </React.Fragment>
  //     ),
  //   })
  // }, [deferredPrompt])

  useEffect(() => {
    closeSnackbar("admin")
    if (!showDemoMessage) closeSnackbar("demo")
    // if (!!state.identity && state.authType === "admin") {
    //   enqueueSnackbar(`${t("Proceed with caution: you are logged in as the administrator.")}`, {
    //     key: "admin",
    //     variant: "info",
    //     persist: true,
    //     preventDuplicate: true,
    //     action: (key) => (
    //       <Button style={{ color: "#fff" }} onClick={() => closeSnackbar(key)}>
    //         {`${t("Dismiss")}`}
    //       </Button>
    //     ),
    //   })
    // } else
    if (showDemoMessage && state.auth?.serverAddress === "demo.spark.digital") {
      enqueueSnackbar(
        `${t("You're logged into a demo account. Any changes you make will be reset when you restart the app.")}`,
        {
          key: "demo",
          variant: "info",
          persist: true,
          preventDuplicate: true,
          action: (key) => (
            <Button style={{ color: "#fff" }} onClick={() => closeSnackbar(key)}>
              {`${t("Dismiss")}`}
            </Button>
          ),
        }
      )
    }
  }, [state])

  let reset = async (identity?: any) => {
    Service.deleteUserDB()
    if (typeof identity === "undefined" && SPARK.Auth._type === "participant") {
      await sensorEventUpdate(null, (state.identity as any)?.id ?? null, null)
      await SPARK.SensorEvent.create((state.identity as any)?.id ?? null, {
        timestamp: Date.now(),
        sensor: "spark.analytics",
        data: {
          type: "logout",
          device_type: "Dashboard",
          user_agent: `SPARK-dashboard/${process.env.REACT_APP_GIT_SHA} ${window.navigator.userAgent}`,
        },
      } as any).then((res) => console.dir(res))
    }

    // Production needs to be set, this will toggle http/https
    if (!!identity) {
      identity["production"] = process.env.REACT_APP_ENV === "production"
    }

    await SPARK.Auth.set_identity(identity).catch((e) => {
      // enqueueSnackbar(`${t("Invalid id or password.")}`, {
      //   variant: "error",
      // })
      return
    })
    if (!!identity) {
      getAdminType()
      let type = {
        identity: SPARK.Auth._me,
        auth: SPARK.Auth._auth,
        authType: SPARK.Auth._type,
      }
      setState((state) => ({ ...state, ...type }))
      return type
    } else {
      setState((state) => ({
        ...state,
        identity: null,
        auth: null,
        authType: null,
        activeTab: null,
        lastDomain: ["api.spark.digital", "demo.spark.digital"].includes(state.auth.serverAddress)
          ? undefined
          : state.auth.serverAddress,
      }))
      window.location.href = "/#/"
    }
  }

  let getResearcher = (id) => {
    if (id === "me" && state.authType === "researcher" && !Array.isArray(state.identity)) {
      id = state.identity.id
    }
    if (!id || id === "me") {
      return null //props.history.replace(`/`)
    }
    if (!!store.researchers[id]) {
      return store.researchers[id]
    } else if (!storeRef.current.includes(id)) {
      SPARK.Researcher.view(id).then((x: any) => {
        setState((state) => ({
          ...state,
          professionalRole: x.role,
        }))
        setStore({
          researchers: { ...store.researchers, [id]: x },
          projects: store.projects,
          participants: store.participants,
        })
      })
      storeRef.current = [...storeRef.current, id]
    }
    return null
  }

  let getProject = (id) => {
    // TODO: role check
    // if (!id || id === "me") {
    //   return null //props.history.replace(`/`)
    // }
    if (!!store.projects[id]) {
      //debug log
      return store.projects[id]
    } else if (!storeRef.current.includes(id)) {
      SPARK.Study.view(id).then((x) =>
        setStore({
          researchers: store.researchers,
          participants: store.participants,
          projects: { ...store.projects, [id]: x },
        })
      )
      storeRef.current = [...storeRef.current, id]
    }
    return null
  }

  let getParticipant = (id) => {
    if (id === "me" && state.authType === "participant" && !Array.isArray(state.identity)) {
      id = state.identity.id
    }
    if (!id || id === "me") {
      return null //props.history.replace(`/`)
    }
    if (!!store.participants[id]) {
      return store.participants[id]
    } else if (!storeRef.current.includes(id)) {
      SPARK.Participant.view(id).then((x) =>
        setStore({
          researchers: store.researchers,
          projects: store.projects,
          participants: { ...store.participants, [id]: x },
        })
      )
      storeRef.current = [...storeRef.current, id]
    }
    return null
  }

  let getParticipantName = (id) => {
    SPARK.Credential.list(id).then((x) => {
      if (x.length > 0) {
        setName(x[0]["access_key"])
      }
    })
    return name
  }

  const submitSurvey = () => {
    setState((state) => ({
      ...state,
      surveyDone: true,
    }))
  }

  // const promptInstall = () => {
  //   if (deferredPrompt === null) return
  //   deferredPrompt.prompt()
  //   deferredPrompt.userChoice.then((c) => {
  //     if (c.outcome === "accepted") {
  //       enqueueSnackbar(`${t("Spark will be installed on your device.")}`, {
  //         variant: "info",
  //       })
  //     } else {
  //       enqueueSnackbar(`${t("Spark will not be installed on your device.")}`, {
  //         variant: "warning",
  //       })
  //     }
  //     setDeferredPrompt(null)
  //   })
  // }

  const updateStore = (id: string) => {
    if (!!store.researchers[id]) {
      SPARK.Researcher.view(id).then((x) => {
        setStore({
          researchers: { ...store.researchers, [id]: x },
          projects: store.projects,
          participants: store.participants,
        })
      })
    }
  }

  return (
    <Switch>
      <Route
        exact
        path="/participant/:id/messages"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Messages")}`}</PageTitle>
              <Messages
                style={{ margin: "0px -16px -16px -16px" }}
                refresh={true}
                participantOnly
                participant={getParticipant(props.match.params.id)?.id ?? null}
              />
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/participant/:id/activity/:activityId"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {t("Login")}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/participant")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <NavigationLayout
                authType={state.authType}
                id={props.match.params.id}
                setIdentity={async (identity) => await reset(identity)}
                title={"Participant" + " " + getParticipantName(props.match.params.id)}
                onLogout={() => reset()}
                activeTab={state.activeTab}
              >
                <NotificationPage
                  participant={props.match.params.id}
                  activityId={props.match.params.activityId}
                  mode={new URLSearchParams(search).get("mode")}
                  tab={state.activeTab}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />

      {/* <Route
        exact
        path="/researcher/:rid/activity/import"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <ImportActivity />
            </React.Fragment>
          )
        }
      /> */}

      <Route
        exact
        path="/researcher/:rid/participant/:id/settings"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PatientProfile researcherId={props.match.params.rid} participantId={props.match.params.id} />
            </React.Fragment>
          )
        }
      />
      <Route
        exact
        path="/researcher/:rid/project/:pid/activity/:id"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <NavigationLayout
                id={props.match.params.id}
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                title={`${getProject(props.match.params.pid)?.name}`}
                goBack={props.history.goBack}
                onLogout={() => reset()}
                activeTab="Projects"
                sameLineTitle={true}
                changeMode={changeMode}
              >
                <Activity
                  id={props.match.params.id}
                  projectId={props.match.params.pid}
                  researcherId={props.match.params.rid}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />
      <Route
        exact
        path="/researcher/:rid/project/:pid/trigger/:id"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>{`${getProject(props.match.params.pid)?.name}`}</PageTitle>
              <NavigationLayout
                id={props.match.params.id}
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                title={`${getProject(props.match.params.pid)?.name}`}
                goBack={props.history.goBack}
                onLogout={() => reset()}
                activeTab="Projects"
                sameLineTitle={true}
                changeMode={changeMode}
              >
                <Trigger
                  id={props.match.params.id}
                  projectId={props.match.params.pid}
                  researcherId={props.match.params.rid}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />
      {/* Route index => login or home (which redirects based on user type). */}
      <Route
        exact
        path="/"
        render={(props) =>
          !(window.location.hash.split("?").length > 1 && !state.identity) ? (
            !state.identity ? (
              <React.Fragment>
                <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
                <Login
                  setIdentity={async (identity) => await reset(identity)}
                  lastDomain={state.lastDomain}
                  onComplete={() => props.history.replace("/")}
                />
              </React.Fragment>
            ) : state.authType === "admin" ? (
              <Redirect to="/organization" />
            ) : state.authType === "researcher" ? ( // TODO: add role check, and show organization if Admin Professional
              <Redirect to="/researcher/me/projects" />
            ) : (
              <Redirect to="/participant/me/home" />
            )
          ) : (
            <React.Fragment />
          )
        }
      />
      {/* Used to let participants register or login */}
      <Route
        exact
        path="/participant"
        render={(props) =>
          !(window.location.hash.split("?").length > 1 && !state.identity) ? (
            !state.identity ? (
              <React.Fragment>
                <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
                <Login
                  setIdentity={async (identity) => await reset(identity)}
                  lastDomain={state.lastDomain}
                  onComplete={() => props.history.replace("/")}
                />
              </React.Fragment>
            ) : (
              <Redirect to="/participant/me/home" />
            )
          ) : (
            <React.Fragment />
          )
        }
      />

      {/* Route authenticated routes. */}
      <Route
        exact
        path="/organization"
        render={(props) =>
          !state.identity || state.authType !== "admin" ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Administrator")}`}</PageTitle>
              <NavigationLayout
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                title={
                  state.adminType === "admin"
                    ? "Administrator"
                    : state.adminType === "practice_lead"
                    ? "Practice Lead"
                    : "User Administrator"
                }
                goBack={props.history.goBack}
                onLogout={() => reset()}
              >
                <Root {...props} updateStore={updateStore} adminType={state.adminType} initialTab={0} />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />
      <Route
        exact
        path="/users"
        render={(props) =>
          !state.identity || state.authType !== "admin" ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Administrator")}`}</PageTitle>
              <NavigationLayout
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                title={
                  state.adminType === "admin"
                    ? "Administrator"
                    : state.adminType === "practice_lead"
                    ? "Practice Lead"
                    : "User Administrator"
                }
                goBack={props.history.goBack}
                onLogout={() => reset()}
              >
                <Root {...props} updateStore={updateStore} adminType={state.adminType} initialTab={2} />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />
      <Route
        exact
        path="/researcher/:id/:tab"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : !getResearcher(props.match.params.id) ? (
            <React.Fragment />
          ) : (
            <React.Fragment>
              <PageTitle>{`${getResearcher(props.match.params.id).name}`}</PageTitle>
              <NavigationLayout
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                id={props.match.params.id}
                title={`${getResearcher(props.match.params.id).name}`}
                goBack={() => {
                  window.location.href = `/#/organization`
                }}
                onLogout={() => reset()}
                activeTab="Researcher"
                sameLineTitle={true}
                changeMode={changeMode}
              >
                <Researcher
                  researcher={getResearcher(props.match.params.id)}
                  mode={state.mode}
                  tab={props.match.params.tab}
                  role={state.professionalRole}
                  authType={state.authType}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/researcher/:rid/project/:id/:tab"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>{`${getProject(props.match.params.id)?.name}`}</PageTitle>
              <NavigationLayout
                id={props.match.params.id}
                authType={state.authType}
                setIdentity={async (identity) => await reset(identity)}
                title={`${getProject(props.match.params.id)?.name}`}
                goBack={() => {
                  window.location.href = `/#/researcher/${props.match.params.rid}/projects`
                }}
                onLogout={() => reset()}
                activeTab="Projects"
                sameLineTitle={true}
                changeMode={changeMode}
              >
                <Project
                  project={`${getProject(props.match.params.id)?.joinable}`}
                  id={props.match.params.id}
                  onParticipantSelect={(id) => {
                    setState((state) => ({
                      ...state,
                      activeTab: 3,
                    }))
                    props.history.push(`/participant/${id}/portal`)
                  }}
                  mode={state.mode}
                  tab={props.match.params.tab}
                  researcherId={props.match.params.rid}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/data_portal"
        render={(props) =>
          !state.identity || (state.authType !== "admin" && state.authType !== "researcher") ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/data_portal")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PageTitle>Data Portal</PageTitle>
              <DataPortal
                standalone
                token={{
                  username: SPARK.Auth._auth.id,
                  password: SPARK.Auth._auth.password,
                  server: SPARK.Auth._auth.serverAddress ? SPARK.Auth._auth.serverAddress : "api.spark.digital",
                  type: state.authType === "admin" ? "Administrator" : "Researcher",
                  //@ts-ignore: state.identity will have an id param if not admin
                  id: state.authType === "admin" ? null : state.identity.id,
                  //@ts-ignore: state.identity will have an name param if not admin
                  name: state.authType === "admin" ? "Administrator" : state.identity.name,
                }}
                onLogout={() => reset()}
              />
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/participant/:id/permissions"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/participant")}
              />
            </React.Fragment>
          ) : !getParticipant(props.match.params.id) ? (
            <React.Fragment />
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Participant")}` + " " + getParticipantName(props.match.params.id)}</PageTitle>
              <NavigationLayout
                authType={state.authType}
                id={props.match.params.id}
                setIdentity={async (identity) => await reset(identity)}
                title={"Participant" + " " + getParticipantName(props.match.params.id)}
                goBack={() => (window.location.href = ``)}
                onLogout={() => reset()}
                activeTab={"Permissions"}
              >
                <Permissions
                  participant={getParticipant(props.match.params.id)}
                  onComplete={() => (window.location.href = ``)}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/participant/:id/:tab"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/participant")}
              />
            </React.Fragment>
          ) : !getParticipant(props.match.params.id) ? (
            <React.Fragment />
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Participant")}` + " " + getParticipantName(props.match.params.id)}</PageTitle>
              <NavigationLayout //FIXME: navigate back to screen with participants
                authType={state.authType}
                id={props.match.params.id}
                setIdentity={async (identity) => await reset(identity)}
                title={"Participant" + " " + getParticipantName(props.match.params.id)}
                goBack={props.history.goBack}
                onLogout={() => reset()}
                activeTab={state.activeTab}
              >
                <Participant
                  participant={getParticipant(props.match.params.id)}
                  activeTab={activeTab}
                  tabValue={props.match.params.tab}
                  surveyDone={state.surveyDone}
                  submitSurvey={submitSurvey}
                  setShowDemoMessage={(val) => {
                    setShowDemoMessage(val)
                  }}
                />
              </NavigationLayout>
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/participant/:id/portal/activity/:activityId"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/participant")}
              />
            </React.Fragment>
          ) : !getParticipant(props.match.params.id) ? (
            <React.Fragment />
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Participant")}` + " " + getParticipantName(props.match.params.id)}</PageTitle>
              <PreventPage
                type="activity"
                activityId={props.match.params.activityId}
                participantId={props.match.params.id}
              />
            </React.Fragment>
          )
        }
      />

      <Route
        exact
        path="/participant/:id/portal/sensor/:spec"
        render={(props) =>
          !state.identity ? (
            <React.Fragment>
              <PageTitle>Spark | {`${t("Login")}`}</PageTitle>
              <Login
                setIdentity={async (identity) => await reset(identity)}
                lastDomain={state.lastDomain}
                onComplete={() => props.history.replace("/participant")}
              />
            </React.Fragment>
          ) : !getParticipant(props.match.params.id) ? (
            <React.Fragment />
          ) : (
            <React.Fragment>
              <PageTitle>{`${t("Participant")}` + " " + getParticipantName(props.match.params.id)}</PageTitle>
              <PreventPage type="sensor" activityId={props.match.params.spec} participantId={props.match.params.id} />
            </React.Fragment>
          )
        }
      />
    </Switch>
  )
}

export default function App({ ...props }) {
  const theme = createTheme({
    typography: {
      fontFamily: schema.typography.fontFamily.join(","),
    },
    palette: {
      primary: {
        main: schema.palette.primary.main,
      },
      secondary: {
        main: schema.palette.secondary,
      },
      background: {
        default: schema.palette.background,
      },
    },
    overrides: {
      MuiBottomNavigationAction: {
        label: {
          letterSpacing: `0.1em`,
          textTransform: "uppercase",
        },
      },
      MuiButton: {
        root: {
          textTransform: "none",
        },
      },
      MuiFilledInput: {
        root: {
          border: 0,
          backgroundColor: "#f4f4f4",
        },
        underline: {
          "&&&:before": {
            borderBottom: "none",
          },
          "&&:after": {
            borderBottom: "none",
          },
        },
      },
      MuiTextField: {
        root: { width: "100%" },
      },
      MuiTableCell: {
        root: {
          borderBottom: "#fff solid 1px",
          padding: 10,
        },
      },
      MuiTypography: {
        h6: { fontSize: 16, fontWeight: 600 },
      },
      MuiDivider: {
        root: { margin: "25px 0" },
      },
      MuiStepper: {
        root: { paddingLeft: 8 },
      },
    },
  })

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <SnackbarProvider>
            <HashRouter>
              <AppRouter {...props} />
            </HashRouter>
          </SnackbarProvider>
        </MuiPickersUtilsProvider>
        <span
          style={{
            position: "fixed",
            bottom: 16,
            left: 16,
            fontSize: "8",
            zIndex: -1,
            opacity: 0.1,
          }}
        >
          {`v${process.env.REACT_APP_GIT_NUM} (${process.env.REACT_APP_GIT_SHA})}`}
        </span>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
